import {
  EuiCallOut,
  EuiMarkdownFormat,
  EuiPanel,
  EuiSkeletonText,
  EuiTabbedContent,
  EuiTabbedContentTab,
  EuiText,
} from "@inscopix/ideas-eui";
import { ToolSpecViewer } from "../ToolSpecViewer/ToolSpecViewer";
import { useQuery } from "@tanstack/react-query";
import { getEnvVar } from "ideas.env";
import { getRequestHeaders } from "utils/getRequestHeaders";
import axios from "axios";
import { Tag } from "components/ToolBrowser/Tag";
import moment from "moment";
import { filesize } from "utils/filesize";
import { ToolDataContextValue } from "pages/tool/ToolDataProvider";
import { isToolSpecV3 } from "types/json-schemas/type_guards";
import { CallOutError } from "components/CallOutError/CallOutError";

interface ToolPanelVersionDetailProps {
  toolVersion: ToolDataContextValue["tool"]["tool_versions"][number];
}

type ContainerImageDetails = {
  id: number;
  name: string;
  label: string;
  date_created: string;
  date_updated: string;
  tags: string[] | null;
  description: string | null;
  readme: string | null;
  size: number;
  user: string | null;
  tenant: number;
};

const useGetContainerImageQuery = (containerId: number) => {
  return useQuery<ContainerImageDetails, Error>({
    queryFn: async () => {
      const url = getEnvVar("URL_TES_CONTAINER_DETAILS")(containerId);
      const headers = await getRequestHeaders();
      const res = await axios.get<ContainerImageDetails>(url, { headers });
      return res.data;
    },
  });
};

export interface ContainerImageDetailProps {
  containerImageId: number;
}

const ContainerImageDetail = ({
  containerImageId,
}: ContainerImageDetailProps) => {
  const { data, isLoading, error } =
    useGetContainerImageQuery(containerImageId);

  if (isLoading) {
    return <EuiSkeletonText lines={10} />;
  }

  if (error) {
    return (
      <EuiCallOut
        title={"Error loading container information"}
        color="danger"
      />
    );
  }

  return (
    <EuiText size="s">
      <dl>
        <dt>Name:</dt>
        <dd>{data?.name}</dd>

        <dt>Description:</dt>
        <dd>
          {data.description === "" || data.description === null
            ? "None"
            : data.description}
        </dd>

        <dt>Created:</dt>
        <dd>{moment(data.date_created).fromNow()}</dd>

        <dt>Updated:</dt>
        <dd>{moment(data.date_updated).fromNow()}</dd>

        <dt>Storage:</dt>
        <dd>{filesize(data.size)}</dd>

        <dt>Tags:</dt>
        <dd>
          {data.tags
            ? data.tags.map((tag) => <Tag key={tag} tag={tag} />)
            : "None"}
        </dd>

        <dt>Overview:</dt>
        <dd>
          {data.readme !== null && data.readme !== "" ? (
            <EuiPanel hasBorder>
              <EuiMarkdownFormat>{data.readme ?? ""}</EuiMarkdownFormat>
            </EuiPanel>
          ) : (
            "None"
          )}
        </dd>
      </dl>
    </EuiText>
  );
};

export const ToolPanelVersionDetail = ({
  toolVersion,
}: ToolPanelVersionDetailProps) => {
  const toolSpec = toolVersion.tool_spec;
  const tabs: EuiTabbedContentTab[] = [
    {
      id: "specification",
      name: "Specification",
      content: isToolSpecV3(toolSpec) ? (
        <ToolSpecViewer toolSpec={toolSpec} />
      ) : (
        <CallOutError />
      ),
    },
  ];
  if (toolVersion.changelog !== null && toolVersion.changelog.trim() !== "") {
    tabs.push({
      id: "changelog",
      name: "Changelog",
      content: (
        <EuiMarkdownFormat textSize="s">
          {toolVersion.changelog}
        </EuiMarkdownFormat>
      ),
    });
  }
  if (toolVersion.container_image !== null) {
    tabs.push({
      id: "container-image",
      name: "Container Image",
      content: (
        <ContainerImageDetail containerImageId={toolVersion.container_image} />
      ),
    });
  }
  return (
    <EuiTabbedContent
      tabs={tabs}
      initialSelectedTab={tabs[0]}
      autoFocus="selected"
    />
  );
};
